import { Suspense, lazy, useEffect, useState } from 'react';
import {
  type Location,
  Navigate,
  Outlet,
  type RouteObject,
  ScrollRestoration,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';
import { Landing } from './Landing';
import { legalRoutes } from './Legal';
import { NotFound } from './NotFound';
import { Signin } from './Signin';
import { type ActorPermissions, getPermissions, getRole } from './api';
import { RouteError } from './components/RouteError';
import { SkeletonScreen } from './components/ui/Skeleton';
import { OwnerHome } from './owner/OwnerHome';
import { OwnerLayout } from './owner/OwnerLayout';
import { OwnerLogin } from './owner/OwnerLogin';
import { StaffLayout } from './staff/StaffLayout';
import { StaffLogin } from './staff/StaffLogin';
import { StaffToday } from './staff/StaffToday';

/**
 * Everything past the first screen is loaded when it is opened.
 *
 * One bundle meant a housekeeper on a phone downloaded the payroll screen, the
 * money screen and the audit log before she could see today's work — 850KB to
 * answer "what am I doing this morning". These are the screens nobody is
 * looking at on arrival.
 *
 * Landing, both layouts and the sign-in form stay in the first chunk: they are
 * the first paint, and code-splitting the thing somebody is already waiting for
 * only adds a round trip.
 */
const AcceptInvite = lazy(() =>
  import('./AcceptInvite').then((m) => ({ default: m.AcceptInvite })),
);
const AccountSecurity = lazy(() =>
  import('./AccountSecurity').then((m) => ({ default: m.AccountSecurity })),
);
const ActivityLog = lazy(() =>
  import('./owner/ActivityLog').then((m) => ({ default: m.ActivityLog })),
);
const ForgotPassword = lazy(() =>
  import('./ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() =>
  import('./ResetPassword').then((m) => ({ default: m.ResetPassword })),
);
const EventDetail = lazy(() =>
  import('./components/EventDetail').then((m) => ({ default: m.EventDetail })),
);
const HouseRules = lazy(() =>
  import('./components/HouseRules').then((m) => ({ default: m.HouseRules })),
);
const Notes = lazy(() => import('./components/Notes').then((m) => ({ default: m.Notes })));
const NotificationsPage = lazy(() =>
  import('./components/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const Onboarding = lazy(() =>
  import('./owner/Onboarding').then((m) => ({ default: m.Onboarding })),
);
const OperationDetail = lazy(() =>
  import('./owner/OperationDetail').then((m) => ({ default: m.OperationDetail })),
);
const OwnerMoney = lazy(() =>
  import('./owner/OwnerMoney').then((m) => ({ default: m.OwnerMoney })),
);
const OwnerPayroll = lazy(() =>
  import('./owner/OwnerPayroll').then((m) => ({ default: m.OwnerPayroll })),
);
const OwnerPulse = lazy(() =>
  import('./owner/OwnerPulse').then((m) => ({ default: m.OwnerPulse })),
);
const OwnerRoutines = lazy(() =>
  import('./owner/OwnerRoutines').then((m) => ({ default: m.OwnerRoutines })),
);
const OwnerSignup = lazy(() =>
  import('./owner/OwnerSignup').then((m) => ({ default: m.OwnerSignup })),
);
const OwnerStaff = lazy(() =>
  import('./owner/OwnerStaff').then((m) => ({ default: m.OwnerStaff })),
);
const OwnerTasks = lazy(() =>
  import('./owner/OwnerTasks').then((m) => ({ default: m.OwnerTasks })),
);
const OwnerVault = lazy(() => import('./owner/Vault').then((m) => ({ default: m.OwnerVault })));
const OwnerStorage = lazy(() =>
  import('./owner/Storage').then((m) => ({ default: m.OwnerStorage })),
);
const PersonPage = lazy(() =>
  import('./owner/PersonPage').then((m) => ({ default: m.PersonPage })),
);
const Profile = lazy(() => import('./Profile').then((m) => ({ default: m.Profile })));
const PayslipPrint = lazy(() =>
  import('./PayslipPrint').then((m) => ({ default: m.PayslipPrint })),
);
const Search = lazy(() => import('./components/Search').then((m) => ({ default: m.Search })));
const StaffAcceptInvite = lazy(() =>
  import('./StaffAcceptInvite').then((m) => ({ default: m.StaffAcceptInvite })),
);
const StaffHistory = lazy(() =>
  import('./staff/StaffHistory').then((m) => ({ default: m.StaffHistory })),
);
const StaffMore = lazy(() => import('./staff/StaffMore').then((m) => ({ default: m.StaffMore })));
const StaffPay = lazy(() => import('./staff/StaffPay').then((m) => ({ default: m.StaffPay })));
const StaffReceipt = lazy(() =>
  import('./staff/StaffReceipt').then((m) => ({ default: m.StaffReceipt })),
);
const WeeklyPlan = lazy(() =>
  import('./owner/WeeklyPlan').then((m) => ({ default: m.WeeklyPlan })),
);
const PilotWorkspace = lazy(() =>
  import('./research/PilotWorkspace').then((m) => ({ default: m.PilotWorkspace })),
);
const ChainInspector = lazy(() =>
  import('./chains/ChainInspector').then((m) => ({ default: m.ChainInspector })),
);
const PlannerReview = lazy(() =>
  import('./chains/PlannerReview').then((m) => ({ default: m.PlannerReview })),
);
const ContextInspector = lazy(() =>
  import('./context/ContextInspector').then((m) => ({ default: m.ContextInspector })),
);
const ContextCandidates = lazy(() =>
  import('./context/ContextCandidates').then((m) => ({ default: m.ContextCandidates })),
);
const GapInspector = lazy(() =>
  import('./gaps/GapInspector').then((m) => ({ default: m.GapInspector })),
);
const Clarifications = lazy(() =>
  import('./gaps/Clarifications').then((m) => ({ default: m.Clarifications })),
);
const NextAction = lazy(() =>
  import('./worker/NextAction').then((m) => ({ default: m.NextAction })),
);
const ObligationDiagnostics = lazy(() =>
  import('./owner/ObligationDiagnostics').then((m) => ({ default: m.ObligationDiagnostics })),
);
const ReleaseReadiness = lazy(() =>
  import('./owner/ReleaseReadiness').then((m) => ({ default: m.ReleaseReadiness })),
);
const WorkerTransition = lazy(() =>
  import('./owner/WorkerTransition').then((m) => ({ default: m.WorkerTransition })),
);
const AuthorityPolicies = lazy(() =>
  import('./owner/AuthorityPolicies').then((m) => ({ default: m.AuthorityPolicies })),
);

/**
 * What a route shows while its chunk is arriving. The skeleton the screens
 * already use, so a slow connection reads as loading rather than as broken.
 */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<SkeletonScreen rows={3} />}>{children}</Suspense>;
}

/**
 * The frame every route hangs off.
 *
 * Pathless, so every absolute path below is unchanged. It exists for the two
 * things that have to live inside the data router: scroll restoration (without
 * it, coming back from a task to a screenful of tasks landed at the top of the
 * list every time), and an error element for the handful of routes that carry
 * no layout of their own.
 */
function Root() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

/**
 * A crash inside one screen stays inside that screen.
 *
 * Wraps a layout's pages in one pathless route carrying the error element, so
 * React Router renders the error *in the layout's Outlet*. Putting the same
 * element on the layout route instead would replace the layout — which is the
 * blank page this is here to stop.
 */
function shielded(children: RouteObject[]): RouteObject[] {
  return [{ errorElement: <RouteError />, children }];
}

/**
 * Sign in, and then carry on to where you were going.
 *
 * A push notification opens /op/<id>; if the session had expired, the guard
 * fired and the address was thrown away — the person signed in and landed on
 * /home with no idea what they had been told to look at. The destination rides
 * along as `?next=`, a query param rather than location state because the 401
 * path in api.ts is a full page load and state does not survive one.
 */
function signInPath(location: Location, base = '/login'): string {
  const next = `${location.pathname}${location.search}`;
  if (next === '/' || next.startsWith(base)) return base;
  return `${base}?next=${encodeURIComponent(next)}`;
}

/**
 * Signing out in one tab used to leave every other tab rendering owner screens
 * until something 401'd. The guards read localStorage during render and never
 * heard about it; this makes them re-read when another tab writes.
 */
function useSessionChanges(): void {
  const [, bump] = useState(0);
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      // key is null on a whole-store clear(), which is exactly the sign-out.
      if (e.key === null || e.key.startsWith('fm.')) bump((n) => n + 1);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
}

function RequireOwner() {
  useSessionChanges();
  const location = useLocation();
  const role = getRole();
  if (role !== 'owner') return <Navigate to={signInPath(location)} replace />;
  return <Outlet />;
}
function RequireStaff() {
  useSessionChanges();
  const location = useLocation();
  const role = getRole();
  if (role !== 'staff') return <Navigate to={signInPath(location, '/staff/login')} replace />;
  return <Outlet />;
}
function RequireAny() {
  useSessionChanges();
  const location = useLocation();
  const role = getRole();
  // The sign-in form and not the landing page: everything behind this guard is
  // something a notification linked to, and the landing page cannot hand the
  // address back afterwards.
  if (!role) return <Navigate to={signInPath(location)} replace />;
  return <Outlet />;
}

/**
 * Open to the owner, and to a staff member the owner trusted with this
 * permission.
 *
 * These three screens used to sit behind RequireOwner, which is why granting
 * "hand out work" to the house manager changed nothing she could see. The
 * server has always enforced the same permission on every call underneath
 * them, so this guard decides what to offer, not what to allow.
 */
function RequirePermission({
  need,
}: {
  need: keyof ActorPermissions | (keyof ActorPermissions)[];
}) {
  useSessionChanges();
  const location = useLocation();
  const role = getRole();
  if (!role) return <Navigate to={signInPath(location)} replace />;
  if (role === 'owner') return <Outlet />;
  const needed = Array.isArray(need) ? need : [need];
  const perms = getPermissions();
  // /today for everyone was the old answer, and /today is itself behind
  // RequireStaff — so anything that was not staff bounced between the two
  // guards forever. Each branch here lands somewhere the guard on the other
  // end cannot send them back from.
  if (!needed.some((n) => perms[n])) {
    return <Navigate to={role === 'staff' ? '/today' : signInPath(location)} replace />;
  }
  return <Outlet />;
}

function RoleAwareLayout() {
  const role = getRole();
  if (role === 'staff') return <StaffLayout />;
  return <OwnerLayout />;
}

const routes: RouteObject[] = [
  { path: '/', element: <Landing /> },
  // /privacy, /terms and /legal, registered where their copy lives. Ranked
  // above the catch-all below by specificity, not by order.
  ...legalRoutes,
  // Anything else: a human 404, not react-router's bare error screen.
  { path: '*', element: <NotFound /> },
  // Unified sign-in. /login + /staff/login both render the same form,
  // which auto-detects the role from the identifier. The old per-role
  // screens stay registered as fallbacks during the rollout in case any
  // bookmark/email link still points at them.
  { path: '/signin', element: <Signin /> },
  { path: '/login', element: <Signin /> },
  { path: '/_login-owner', element: <OwnerLogin /> },
  {
    path: '/signup',
    element: (
      <Lazy>
        <OwnerSignup />
      </Lazy>
    ),
  },
  { path: '/staff/login', element: <Signin /> },
  { path: '/_login-staff', element: <StaffLogin /> },
  // Both public, and they have to be: somebody who has forgotten their
  // password has no session, so anything behind a guard would bounce them to
  // the sign-in screen they cannot get past. /reset-password is the exact
  // address the server puts in the WhatsApp message it sends.
  {
    path: '/forgot-password',
    element: (
      <Lazy>
        <ForgotPassword />
      </Lazy>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Lazy>
        <ResetPassword />
      </Lazy>
    ),
  },
  {
    element: <RequireAny />,
    children: [
      {
        path: '/pilot',
        element: (
          <Lazy>
            <PilotWorkspace />
          </Lazy>
        ),
      },
      {
        path: '/chains/:id',
        element: (
          <Lazy>
            <ChainInspector />
          </Lazy>
        ),
      },
      {
        path: '/planner-review/:id',
        element: (
          <Lazy>
            <PlannerReview />
          </Lazy>
        ),
      },
      {
        path: '/context/:id',
        element: (
          <Lazy>
            <ContextInspector />
          </Lazy>
        ),
      },
      {
        path: '/context-candidates',
        element: (
          <Lazy>
            <ContextCandidates />
          </Lazy>
        ),
      },
      {
        path: '/worker-transition/:id',
        element: (
          <Lazy>
            <WorkerTransition />
          </Lazy>
        ),
      },
      {
        path: '/authority-policies',
        element: (
          <Lazy>
            <AuthorityPolicies />
          </Lazy>
        ),
      },
      {
        path: '/gap-inspector',
        element: (
          <Lazy>
            <GapInspector />
          </Lazy>
        ),
      },
      {
        path: '/clarifications',
        element: (
          <Lazy>
            <Clarifications />
          </Lazy>
        ),
      },
    ],
  },
  {
    path: '/invite/:token',
    element: (
      <Lazy>
        <AcceptInvite />
      </Lazy>
    ),
  },
  {
    path: '/staff-invite/:token',
    element: (
      <Lazy>
        <StaffAcceptInvite />
      </Lazy>
    ),
  },
  {
    element: <RequireOwner />,
    children: [
      // Onboarding lives outside the OwnerLayout so the nav tabs don't
      // distract from the intake.
      {
        path: '/onboarding',
        element: (
          <Lazy>
            <Onboarding />
          </Lazy>
        ),
      },
      {
        element: <OwnerLayout />,
        children: shielded([
          { path: '/home', element: <OwnerHome /> },
          // Absorbed into the composer, which the round + opens. Kept as a
          // redirect: the address is in browser histories and old push payloads.
          { path: '/quick', element: <Navigate to="/tasks?new=1" replace /> },
          {
            path: '/routines',
            element: (
              <Lazy>
                <OwnerRoutines />
              </Lazy>
            ),
          },
          // The same routines, laid out as the week instead of a list. A
          // second view, not a second feature — writing a cell here creates
          // an ordinary weekly routine.
          {
            path: '/routines/week',
            element: (
              <Lazy>
                <WeeklyPlan />
              </Lazy>
            ),
          },
          {
            path: '/pulse',
            element: (
              <Lazy>
                <OwnerPulse />
              </Lazy>
            ),
          },
          {
            path: '/payroll',
            element: (
              <Lazy>
                <OwnerPayroll />
              </Lazy>
            ),
          },
          // Storage & Media — documents, task photos, proof, receipts, voice,
          // versions and Trash. Dark until vault_v1 is on: with the flag off
          // the screen still shows read, download, export and delete, which the
          // rollback contract requires it never to lose.
          {
            path: '/vault',
            element: (
              <Lazy>
                <OwnerVault />
              </Lazy>
            ),
          },
          // Capacity, grants, Recovery Vault and Storage Boxes. Checkout is
          // gated on storage_billing_v1; everything else here is read-only
          // reporting.
          {
            path: '/storage',
            element: (
              <Lazy>
                <OwnerStorage />
              </Lazy>
            ),
          },
          // Same screen as the staff /rules below — the owner and the
          // administrative PA are editing the same house rules, so they get
          // the same UI rather than two that drift apart.
          {
            path: '/memory',
            element: (
              <Lazy>
                <HouseRules />
              </Lazy>
            ),
          },
          // The audit trail, which has been written to since migration 0000
          // and read by nobody. Owner-only: every row carries an IP address.
          {
            path: '/activity',
            element: (
              <Lazy>
                <ActivityLog />
              </Lazy>
            ),
          },
          {
            path: '/obligations/diagnostics',
            element: (
              <Lazy>
                <ObligationDiagnostics />
              </Lazy>
            ),
          },
          {
            path: '/release/readiness',
            element: (
              <Lazy>
                <ReleaseReadiness />
              </Lazy>
            ),
          },
        ]),
      },
    ],
  },
  // Tasks, the team and the money are not owner-only screens — they are the
  // three permissions the owner can hand out. Same components for both roles,
  // inside whichever layout the viewer belongs to.
  {
    element: <RequirePermission need="assign_work" />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: shielded([
          {
            path: '/tasks',
            element: (
              <Lazy>
                <OwnerTasks />
              </Lazy>
            ),
          },
        ]),
      },
    ],
  },
  {
    // Either permission opens the roster: manage_staff to actually manage
    // it, assign_work because handing out work means knowing who it can go
    // to (the search "People" results already surface for either, and the
    // server's listStaff/getStaffMember accept either — this used to
    // require manage_staff alone, so an assign_work-only staffer following
    // a search result silently bounced back to /today).
    element: <RequirePermission need={['manage_staff', 'assign_work']} />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: shielded([
          {
            path: '/staff',
            element: (
              <Lazy>
                <OwnerStaff />
              </Lazy>
            ),
          },
          // A person's own page — everything about them in one place, which is
          // what a card on People now opens onto.
          {
            path: '/staff/:id',
            element: (
              <Lazy>
                <PersonPage />
              </Lazy>
            ),
          },
        ]),
      },
    ],
  },
  {
    element: <RequirePermission need="see_money" />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: shielded([
          {
            path: '/money',
            element: (
              <Lazy>
                <OwnerMoney />
              </Lazy>
            ),
          },
        ]),
      },
    ],
  },
  {
    element: <RequireStaff />,
    children: [
      {
        element: <StaffLayout />,
        children: shielded([
          { path: '/today', element: <StaffToday /> },
          {
            path: '/today/receipt',
            element: (
              <Lazy>
                <StaffReceipt />
              </Lazy>
            ),
          },
          {
            path: '/history',
            element: (
              <Lazy>
                <StaffHistory />
              </Lazy>
            ),
          },
          {
            path: '/pay',
            element: (
              <Lazy>
                <StaffPay />
              </Lazy>
            ),
          },
          // The house rules. Readable by everyone in the household; the
          // add/edit controls only appear for someone with `assign_work`.
          {
            path: '/rules',
            element: (
              <Lazy>
                <HouseRules />
              </Lazy>
            ),
          },
          // Everything real but not daily, one tap behind the fourth tab.
          {
            path: '/more',
            element: (
              <Lazy>
                <StaffMore />
              </Lazy>
            ),
          },
          {
            path: '/next-action/:chainId',
            element: (
              <Lazy>
                <NextAction />
              </Lazy>
            ),
          },
        ]),
      },
    ],
  },
  // Routes available to both roles — operation detail + profile.
  {
    element: <RequireAny />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: shielded([
          {
            path: '/op/:id',
            element: (
              <Lazy>
                <OperationDetail />
              </Lazy>
            ),
          },
          // The evening a task belongs to. Read by both roles — a housekeeper
          // with one job for Thursday should be able to see what Thursday is.
          // Adding and removing is gated on assign_work inside the screen,
          // and on the server.
          {
            path: '/event/:id',
            element: (
              <Lazy>
                <EventDetail />
              </Lazy>
            ),
          },
          {
            path: '/profile',
            element: (
              <Lazy>
                <Profile />
              </Lazy>
            ),
          },
          // Devices, two-factor and the password. Both roles: a housekeeper
          // has as much reason to end a session on a lost phone as her
          // employer does, and the server scopes every one of these calls to
          // the caller's own account.
          {
            path: '/security',
            element: (
              <Lazy>
                <AccountSecurity />
              </Lazy>
            ),
          },
          // A person's own notes. Both roles, one screen, and the server
          // will not hand one person another's — so there is no guard here
          // beyond "somebody is signed in".
          {
            path: '/notes',
            element: (
              <Lazy>
                <Notes />
              </Lazy>
            ),
          },
          // The inbox as a place. The bell it used to live behind is in a
          // header that only exists on a laptop, so on a phone every
          // notification this app has ever written was unreadable.
          {
            path: '/notifications',
            element: (
              <Lazy>
                <NotificationsPage />
              </Lazy>
            ),
          },
          // One box over the whole house. Cmd-K, or "/" from anywhere.
          {
            path: '/search',
            element: (
              <Lazy>
                <Search />
              </Lazy>
            ),
          },
          // A payslip somebody can keep. Printed by the browser, which already
          // shapes Hebrew, Urdu and Tagalog correctly and needs no embedded
          // fonts — and never leaves a copy of somebody's pay in storage.
          {
            path: '/payslip/:id',
            element: (
              <Lazy>
                <PayslipPrint />
              </Lazy>
            ),
          },
        ]),
      },
    ],
  },
];

export const router = createBrowserRouter([
  { element: <Root />, errorElement: <RouteError />, children: routes },
]);
